import { useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Stack, Text, TextInput, PasswordInput, Button, Anchor } from '@mantine/core';
import { IconLogin, IconUserPlus } from '@tabler/icons-react';
import { useAuthService } from '@actschurch/ccms-sdk';
import { PageIntro } from '@/components/rides/PageIntro';
import { PlanningCentreButton } from '@/components/auth/PlanningCentreButton';
import { PasswordRules, passwordMeetsRules } from '@/components/auth/PasswordRules';
import { ConsentCheckbox } from '@/components/rides/ConsentCheckbox';
import { Callout, PhoneInput, normaliseAnyPhone } from '@/components/ui';
import { useCopy } from '@/lib/settings';

type FieldErrors = Partial<Record<'firstName' | 'surname' | 'cellphone' | 'email' | 'password' | 'consent', string>>;

/** Turn a registration failure into a sentence a member can act on. */
function registerMessage(raw: string): string {
  const m = raw.toLowerCase();
  if (/already|taken|exists|duplicate/.test(m)) return 'There is already an account for this email address. Try signing in, or reset your password.';
  if (/password/.test(m)) return "That password isn't strong enough. Please follow the list under the password box.";
  if (/fetch|network|econn|timeout/.test(m)) return "We couldn't reach Carpool. Check your internet connection and try again.";
  return "We couldn't create your account. Please check your details and try again.";
}

/** Trimmed registration: name, cellphone (needed for Carpool), email, password and the POPIA consent. */
export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authService = useAuthService();
  const { t } = useCopy();
  const formRef = useRef<HTMLFormElement>(null);

  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [cellphone, setCellphone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);

  const redirect = searchParams.get('redirect');
  const redirectQuery = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';

  const validate = (): FieldErrors => {
    const e: FieldErrors = {};
    if (!firstName.trim()) e.firstName = 'Please enter your first name.';
    if (!surname.trim()) e.surname = 'Please enter your surname.';
    if (!normaliseAnyPhone(cellphone)) e.cellphone = 'Please enter your cellphone number, for example 082 123 4567.';
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) e.email = 'Please enter your email address, for example name@gmail.com.';
    if (!passwordMeetsRules(password)) e.password = 'Your password needs everything in the list below.';
    if (!consent) e.consent = 'Please tick the box to agree, so we can arrange your lifts.';
    return e;
  };

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setFormError(null);
    const found = validate();
    setErrors(found);
    const count = Object.values(found).filter(Boolean).length;
    setSummary(count ? (count === 1 ? 'Please fix the one thing marked below.' : `Please fix the ${count} things marked below.`) : null);
    if (count) {
      // Take the member to the first thing that needs fixing.
      window.requestAnimationFrame(() => {
        const first = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"], [data-invalid="true"] input');
        first?.focus();
        first?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
      return;
    }
    setSubmitting(true);
    try {
      await authService.register({
        email: email.trim(),
        password,
        confirmPassword: password,
        firstName: firstName.trim(),
        surname: surname.trim(),
        cellphone: cellphone.trim(),
        appUrl: window.location.origin,
      });
      navigate(`/auth/confirm-email?email=${encodeURIComponent(email.trim())}${redirect ? `&redirect=${encodeURIComponent(redirect)}` : ''}`, { replace: true });
    } catch (err: any) {
      setFormError(registerMessage(String(err?.message || '')));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const clear = (k: keyof FieldErrors) => setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));

  return (
    <div className="rides-main">
      <PageIntro title="Create your account" lede={t('verify.rider_intro')} />
      <div className="rides-card" style={{ padding: 18 }}>
        <PlanningCentreButton redirectTo={redirect && redirect.startsWith('/') ? redirect : '/home'} />
        <form ref={formRef} onSubmit={onSubmit} noValidate>
          <Stack gap="md">
            {formError && <Callout tone="bad" live>{formError}</Callout>}
            {!formError && summary && <Callout tone="bad" live>{summary}</Callout>}
            <TextInput label="First name" required value={firstName} onChange={(e) => { setFirstName(e.currentTarget.value); clear('firstName'); }} autoComplete="given-name" error={errors.firstName} />
            <TextInput label="Surname" required value={surname} onChange={(e) => { setSurname(e.currentTarget.value); clear('surname'); }} autoComplete="family-name" error={errors.surname} />
            <PhoneInput required value={cellphone} onChange={(v) => { setCellphone(v); clear('cellphone'); }} description={t('register.phone_help')} error={errors.cellphone} />
            <TextInput
              label="Email address"
              type="email"
              required
              value={email}
              onChange={(e) => { setEmail(e.currentTarget.value); clear('email'); }}
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              description={t('register.email_help')}
              error={errors.email}
            />
            <div>
              <PasswordInput
                label="Choose a password"
                required
                value={password}
                onChange={(e) => { setPassword(e.currentTarget.value); clear('password'); }}
                autoComplete="new-password"
                error={errors.password}
                aria-describedby="register-password-rules"
                mb={8}
              />
              <PasswordRules password={password} id="register-password-rules" />
            </div>
            <div data-invalid={errors.consent ? 'true' : undefined}>
              <ConsentCheckbox checked={consent} onChange={(v) => { setConsent(v); clear('consent'); }} />
              {errors.consent && <Text c="red" size="sm" fw={500} mt={6} role="alert">{errors.consent}</Text>}
            </div>
            <Text size="sm" c="dimmed">
              {t('consent.why_we_ask')} Read the <Anchor component={Link} to="/privacy">privacy notice</Anchor> and the <Anchor component={Link} to="/terms">terms of use</Anchor>.
            </Text>
            <Button type="submit" loading={submitting} leftSection={<IconUserPlus size={20} />} fullWidth>Create account</Button>
          </Stack>
        </form>
      </div>
      <Text ta="center" c="dimmed" mt="xl" mb={8}>Already have an account?</Text>
      <Button component={Link} to={`/auth/login${redirectQuery}`} variant="default" fullWidth leftSection={<IconLogin size={20} />}>
        Sign in
      </Button>
    </div>
  );
}
